"use client";

import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Chip,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { documentCache } from "@/utils/documentCache";
import { Citation } from "@/types/citations";
import { detectDocumentType, DocumentType } from "@/utils/fileUtils";
import { useAuthenticatedFileFetcher } from "@/hooks/chat/useAuthenticatedFileFetcher";
import { logger } from "@/telemetry";

import { PDFViewer } from "./viewers/PDFViewer";
import { DOCXViewer } from "./viewers/DOCXViewer";
import { PPTXViewer } from "./viewers/PPTXViewer";
import { ImageViewer } from "./viewers/ImageViewer";
import { TXTViewer } from "./viewers/TXTViewer";
import { CSVViewer } from "./viewers/CSVViewer";
import { ExcelViewer } from "./viewers/ExcelViewer";

export interface CitationDocumentViewerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  citation: Citation | null;
}

/**
 * Modal component for viewing citations in their original documents
 * Downloads document, renders with appropriate viewer (PDF/DOCX), and highlights citation text
 */
export const CitationDocumentViewer: React.FC<CitationDocumentViewerProps> = ({
  isOpen,
  onOpenChange,
  citation,
}) => {
  const { t } = useTranslation();
  const fileFetcher = useAuthenticatedFileFetcher();
  const [documentData, setDocumentData] = React.useState<ArrayBuffer | null>(null);
  const [documentType, setDocumentType] = React.useState<DocumentType>("unknown");

  // Download document when citation changes
  React.useEffect(() => {
    if (!citation || !isOpen) return;

    const downloadDocument = async () => {
      // Check cache first
      const cached = documentCache.get(citation.document_id);

      if (cached) {
        logger.info("📦 Using cached document", { documentId: citation.document_id });
        // Create a fresh ArrayBuffer from the cached Uint8Array
        // This prevents detachment issues with PDF.js
        const arrayBuffer = new ArrayBuffer(cached.data.byteLength);
        const view = new Uint8Array(arrayBuffer);

        view.set(cached.data);
        setDocumentData(arrayBuffer);
        setDocumentType(detectDocumentType(citation.filename));

        return;
      }

      // Download document using hook
      logger.info("⬇️ Downloading document", { documentId: citation.document_id });
      await fileFetcher.fetchFile(citation.document_id, citation.filename, {
        asArrayBuffer: true,
      });
    };

    downloadDocument();
  }, [citation, isOpen]);

  // Handle fetched data: cache and set state
  React.useEffect(() => {
    if (fileFetcher.data.arrayBuffer && citation) {
      // IMPORTANT: Cache FIRST before PDF.js can detach the ArrayBuffer
      // The cache.set() converts it to Uint8Array immediately
      documentCache.set(
        citation.document_id,
        fileFetcher.data.arrayBuffer,
        undefined,
        fileFetcher.data.mimeType || undefined,
      );

      // Now get it back from cache as a fresh ArrayBuffer
      const cachedDoc = documentCache.get(citation.document_id);

      if (cachedDoc) {
        const freshArrayBuffer = new ArrayBuffer(cachedDoc.data.byteLength);
        const view = new Uint8Array(freshArrayBuffer);

        view.set(cachedDoc.data);
        setDocumentData(freshArrayBuffer);
        setDocumentType(detectDocumentType(citation.filename));

        logger.info("✅ Document downloaded", {
          sizeKB: (fileFetcher.data.arrayBuffer.byteLength / 1024).toFixed(1),
        });
      } else {
        // Fallback (should never happen)
        setDocumentData(fileFetcher.data.arrayBuffer);
        setDocumentType(detectDocumentType(citation.filename));
      }
    }
  }, [fileFetcher.data.arrayBuffer, citation]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setDocumentData(null);
      fileFetcher.cleanup();
    }
  }, [isOpen]);

  const renderViewer = () => {
    if (fileFetcher.loading) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label={t("Loading document...") || "Loading document..."} />
        </div>
      );
    }

    if (fileFetcher.error) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <Icon className="text-danger-500" icon="solar:danger-circle-linear" width={48} />
          <p className="text-danger-500">
            {t("Failed to load document. Please try again.") || fileFetcher.error}
          </p>
          {citation && (
            <div className="bg-default-100 dark:bg-default-100/50 p-4 rounded-medium max-w-2xl">
              <p className="text-sm font-medium mb-2 text-default-800 dark:text-white">
                Citation text:
              </p>
              <p className="text-xs text-default-600 dark:text-default-300">{citation.text}</p>
            </div>
          )}
        </div>
      );
    }

    if (!documentData || !citation) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-default-500 dark:text-default-400">No document to display</p>
        </div>
      );
    }

    if (documentType === "pdf") {
      return <PDFViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "docx") {
      return <DOCXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "pptx") {
      return <PPTXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "image") {
      return <ImageViewer documentData={documentData} filename={citation.filename} />;
    }

    if (documentType === "txt") {
      return <TXTViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "csv") {
      return (
        <CSVViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    if (documentType === "excel") {
      return (
        <ExcelViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Icon className="text-warning-500" icon="solar:file-text-linear" width={48} />
        <p className="text-default-600 dark:text-default-300">Unsupported document type</p>
        <p className="text-sm text-default-500 dark:text-default-400">
          Only PDF, DOCX, PPTX, TXT, CSV/TSV, Excel, and image files are supported
        </p>
      </div>
    );
  };

  return (
    <Modal
      aria-labelledby="citation-modal-title"
      backdrop="blur"
      data-testid="citation-document-viewer-modal"
      isOpen={isOpen}
      size="5xl"
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader
              className="flex items-center gap-3 border-b border-divider pb-3"
              id="citation-modal-title"
            >
              <Icon className="text-primary" icon="solar:book-bookmark-linear" width={24} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {citation && (
                    <Chip color="primary" size="sm" variant="flat">
                      {citation.label}
                    </Chip>
                  )}
                  <span className="text-base font-semibold truncate">
                    {citation?.filename || "Citation"}
                  </span>
                </div>
              </div>
            </ModalHeader>

            <ModalBody aria-label="Document viewer" className="p-0" role="document">
              {renderViewer()}
            </ModalBody>

            <ModalFooter className="border-t border-divider">
              <Button
                color="default"
                data-testid="citation-modal-close-button"
                variant="light"
                onPress={onClose}
              >
                {t("Close") || "Close"}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CitationDocumentViewer;
